import axios from 'axios';
//https://desi-mart-backend-3mil.onrender.com/ 
//http://localhost:5000/api  // https://api.desimart.net/api
//https://desimartbackend-production.up.railway.app/api
const API_BASE_URL = "https://api.desimart.net/api";

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Auth APIs
export const authAPI = {
  register: (userData) => api.post('/auth/register', userData),
  login: (credentials) => api.post('/auth/login', credentials),
  getCurrentUser: () => api.get('/auth/me'),
};

// User APIs
export const userAPI = {
  getProfile: (userId) => api.get(`/users/${userId}`),
  updateProfile: (userId, data) => api.put(`/users/${userId}`, data),
  getNetwork: (userId) => api.get(`/users/${userId}/network`),
  getIncomeSummary: (userId) => api.get(`/users/${userId}/income-summary`),
  getAllUsers: () => api.get('/users/all'),
  getMyDownline: () => api.get('/users/my-downline'),
  getMonthlyBusinessTracker: (params) => api.get('/users/monthly-business-tracker', { params }),
  getWallet: () => api.get('/users/wallet'),
  getDashboardProfile: () => api.get('/users/dashboard-profile'),
  resetPassword: (data) => api.put('/users/reset-password', data),
  verifyCredentials: (data) => api.post('/users/verify-credentials', data),
  getMyDirectChildren: () => api.get('/users/my-direct-children'),
  fundTransfer: (data) => api.post('/users/fund-transfer', data),
  mainToDepositTransfer: (data) => api.post('/users/main-to-deposit-transfer', data),
  getFundTransferHistory: (params) => api.get('/users/fund-transfer-history', { params }),
  getAllFundTransfers: (params) => api.get('/users/admin/all-fund-transfers', { params }),
  getBonanzaProgress: () => api.get('/bonanza/progress'),
  getTeamLevelsHierarchy: () => api.get('/team/levels-hierarchy'),
  editUser: (userId, data) => api.put(`/users/admin/${userId}`, data),
  deleteUser: (userId) => api.delete(`/users/admin/${userId}`),
  getUserPayouts: (params) => api.get('/users/admin/user-payouts', { params }),
  getMainDepositTransfers: (params) => api.get('/users/admin/main-deposit-transfers', { params }),
};

// Product APIs
export const productAPI = {
  getAllProducts: (params) => api.get('/products', { params }),
  getProduct: (id) => api.get(`/products/${id}`),
  createProduct: (data) => api.post('/products', data),
  updateProduct: (id, data) => api.put(`/products/${id}`, data),
  deleteProduct: (id) => api.delete(`/products/${id}`),
  getCategories: () => api.get('/products/categories/all'),
};

// Purchase APIs
export const purchaseAPI = {
  createPurchase: (data) => api.post('/purchases/create', data),
  createPurchaseWithPaymentProof: (data) => api.post('/purchases/create-with-payment-proof', data),
  getMyPurchases: (params) => api.get('/purchases/my-purchases', { params }),
  getMonthlyLegReport: (params) => api.get('/purchases/monthly-leg-report', { params }),
  getPurchase: (id) => api.get(`/purchases/${id}`),
  confirmPurchase: (id) => api.put(`/purchases/${id}/confirm`),
  cancelPurchase: (id) => api.put(`/purchases/${id}/cancel`),
  adminConfirmPurchase: (id) => api.put(`/purchases/${id}/admin-confirm`),
  adminCancelPurchase: (id) => api.put(`/purchases/${id}/admin-cancel`),
  approveBankTransferPayment: (id, data) => api.put(`/purchases/admin/approve-payment/${id}`, data),
  rejectBankTransferPayment: (id, data) => api.put(`/purchases/admin/reject-payment/${id}`, data),
  getAllPurchases: () => api.get('/purchases/all'),
};

// Income APIs
export const incomeAPI = {
  getMyIncome: (params) => api.get('/income/my-income', { params }),
  getIncomeByTypes: () => api.get('/income/types'),
  processPurchase: (data) => api.post('/income/process-purchase', data),
  getIncomeSummary: () => api.get('/income/summary'),
  getAllIncome: () => api.get('/income/all'),
  triggerDailyBoosting: () => api.post('/income/trigger-daily-boosting'),
  processDailyBoosting: () => api.post('/income/process-daily-boosting'),
};

// Payout APIs
export const payoutAPI = {
  requestPayout: (data) => api.post('/payouts/request', data),
  getMyPayouts: (params) => api.get('/payouts/my-payouts', { params }),
  getPayout: (id) => api.get(`/payouts/${id}`),
  approvePayout: (id) => api.put(`/payouts/${id}/approve`),
  completePayout: (id, data) => api.put(`/payouts/${id}/complete`, data),
  rejectPayout: (id, data) => api.put(`/payouts/${id}/reject`, data),

  // FIX: param renamed from requestIndex to requestId — now passes subdocument _id
  approveWithdrawal: (userId, requestId, data) =>
    api.put(`/payouts/approve/${userId}/${requestId}`, data),

  // FIX: param renamed from requestIndex to requestId — now passes subdocument _id
  rejectWithdrawal: (userId, requestId, data) =>
    api.put(`/payouts/reject/${userId}/${requestId}`, data),

  getAllWithdrawalRequests: (params) => api.get('/payouts/admin/all-requests', { params }),
  getWalletTransactions: () => api.get('/users/wallet-transactions'),
  updateTransactionPassword: (data) => api.put('/payouts/transaction-password', data),
  hasTransactionPassword: () => api.get('/payouts/has-transaction-password'),
};

// Rank APIs
export const rankAPI = {
  getAllRanks: () => api.get('/ranks/all'),
  getMyRank: () => api.get('/ranks/my-rank'),
  getRank: (rankName) => api.get(`/ranks/${rankName}`),
  initRanks: () => api.post('/ranks/init'),
};

// Admin APIs
export const adminAPI = {
  seedProducts: () => api.post('/admin/seed-products'),
  getStats: () => api.get('/admin/stats'),
  transferFunds: (data) => api.post('/users/transfer-funds', data),
  getTransferUsers: (params) => api.get('/users/admin/transfer-users', { params }),
  getWalletTransactions: () => api.get('/users/wallet-transactions'),
};

// KYC APIs
export const kycAPI = {
  submitKyc: (formData) => api.post('/kyc/submit', formData),
  updateKyc: (formData) => api.put('/kyc/update', formData),
  updateQrCode: (qrCodeUrl) => api.put('/kyc/update-qr-code', { qrCode: qrCodeUrl }),
  getKycStatus: () => api.get('/kyc/status'),
};

// Contact APIs
export const contactAPI = {
  submitContact: (data) => api.post('/contact', data),
  getContacts: (params) => api.get('/contact', { params }),
  getContact: (id) => api.get(`/contact/${id}`),
  updateContactStatus: (id, data) => api.put(`/contact/${id}/status`, data),
  deleteContact: (id) => api.delete(`/contact/${id}`),
  getContactStats: () => api.get('/contact/stats'),
};

// Payment APIs
export const paymentAPI = {
  submitPaymentProof: (data) => api.post('/payments/submit', data),
  getMyPayments: () => api.get('/payments/my'),
  getAdminPaymentHistory: () => api.get('/payments/admin/history'),
  approvePayment: (id, data) => api.put(`/payments/admin/approve/${id}`, data),
  rejectPayment: (id, data) => api.put(`/payments/admin/reject/${id}`, data),
  deletePayment: (id, data) => api.put(`/payments/admin/delete/${id}`, data),
};

// Investment Plans APIs
export const investmentPlanAPI = {
  getUsersBusiness: (params) => api.get('/investment-plans/users-business', { params }),
  createPlan: (data) => api.post('/investment-plans/create-plan', data),
  distributeReward: (data) => api.post('/investment-plans/distribute-reward', data),
  getRewardHistory: (userId) => api.get(`/investment-plans/reward-history/${userId}`),
  getSummary: () => api.get('/investment-plans/summary'),
  getUserDashboard: () => api.get('/investment-plans/user-dashboard'),
  claimReward: (data) => api.post('/investment-plans/claim-reward', data),
};

// Lifetime Rewards APIs
export const lifetimeRewardsAPI = {
  getQualifiedUsers: () => api.get('/lifetime-rewards/qualified-users'),
  getAllUsers: () => api.get('/lifetime-rewards/all-users'),
  getMyDashboard: () => api.get('/lifetime-rewards/my-dashboard'),
  claimMatchingBonus: () => api.post('/lifetime-rewards/claim-matching-bonus'),
  calculateBinaryTree: (userId) => api.post(`/lifetime-rewards/calculate/${userId}`),
  distributeReward: (data) => api.post('/lifetime-rewards/distribute-reward', data),
  getUserRewards: (userId) => api.get(`/lifetime-rewards/user/${userId}`),
  getSummary: () => api.get('/lifetime-rewards/summary'),
  cleanupIncorrectAchievements: () => api.post('/lifetime-rewards/cleanup-incorrect-achievements'),
};

export default api;